/**
 * Drop — capture an idea in about 30 seconds.
 *
 * The AI shape step is a guide moment: it proposes a cleaner title/description,
 * and accepting is one tap. Every AI touchpoint is double-guarded — hidden when
 * the capability is off (`useAiCapability`) and wrapped in try/catch so the
 * human path always works (these services throw rather than degrade).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Send, Sparkles } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { ITEM_CREATE_BALL, ITEM_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';
import { useItemCategoriesStore } from '@/stores/prioritization/itemCategoriesStore';
import { useAiCapability } from '@/lib/ai/useAiCapability';
import { craftIdea } from '@/lib/llmIdeaCraftingService';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { findDuplicateMatch, type DuplicateMatch } from '@/lib/whiteboardImportService';
import type { ItemLanguageType } from '@/types/prioritization/lifecycle';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, Eyebrow, PrimaryButton, SecondaryButton } from '../components/primitives';

// The six built-in categories — shown when the workspace has none configured yet.
const KINDS: Array<{ value: ItemLanguageType; label: string; hint: string }> = [
  { value: 'problem', label: 'Problem', hint: 'Something that needs fixing' },
  { value: 'opportunity', label: 'Opportunity', hint: 'Something we could gain' },
  { value: 'risk', label: 'Risk', hint: 'Something that might go wrong' },
  { value: 'solution', label: 'Solution', hint: 'A way to address something' },
  { value: 'commitment', label: 'Commitment', hint: 'A promise we have to keep' },
  { value: 'investment', label: 'Investment', hint: 'A bet that pays off later' },
];

export default function DropScreen() {
  const app = getAppDefinition('intake')!;
  const navigate = useNavigate();
  const createItem = useItemsStore((s) => s.createItem);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const wsCategories = useItemCategoriesStore((s) => s.categories);
  const fetchCategories = useItemCategoriesStore((s) => s.fetchCategories);
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  // Sending an idea inserts an item, whose insert rule (migration 751) takes
  // items.create: the form is drawn only for holders (build contract §2.8).
  const mayCreateItems = useCreateRights().items;
  const craftEnabled = useAiCapability('idea_crafting');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<ItemLanguageType | ''>('');
  const [suggestion, setSuggestion] = useState<{ title: string; description: string } | null>(null);
  const [shaping, setShaping] = useState(false);
  const [shapeError, setShapeError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateMatch | null>(null);

  // The duplicate check needs the workspace's items; the kinds come from its settings.
  useEffect(() => {
    void fetchItems();
    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId) void fetchCategories(workspaceId);
  }, [fetchItems, fetchCategories]);

  // Same rule as the item form: the workspace's own active categories, else the six built-ins.
  const activeCategories = wsCategories.filter((c) => c.isActive);
  const kinds =
    activeCategories.length > 0
      ? activeCategories.map((c) => ({
          value: c.slug as ItemLanguageType,
          label: c.label,
          hint: c.description || KINDS.find((k) => k.value === c.slug)?.hint || '',
        }))
      : KINDS;

  const canSubmit = title.trim().length > 0 && !submitting && !duplicate;

  const shape = async () => {
    setShaping(true);
    setShapeError(null);
    try {
      const result = await craftIdea({ title: title.trim(), description: description.trim() });
      setSuggestion({
        title: result.suggestedTitle ?? title,
        description: result.suggestedDescription ?? description,
      });
    } catch {
      // Never block the human path on an AI failure.
      setShapeError("Couldn't shape that right now — your wording works fine.");
    } finally {
      setShaping(false);
    }
  };

  const acceptSuggestion = () => {
    if (!suggestion) return;
    setTitle(suggestion.title);
    setDescription(suggestion.description);
    setSuggestion(null);
    setDuplicate(null);
  };

  const submit = async (skipDuplicateCheck = false) => {
    if (!userId) return;
    if (!skipDuplicateCheck) {
      const match = findDuplicateMatch(
        title.trim(),
        items.filter((i) => i.exit_status !== 'archived'),
      );
      if (match) {
        setDuplicate(match);
        return;
      }
    }
    setDuplicate(null);
    setSubmitting(true);
    setError(null);
    const ok = await createItem({
      title: title.trim(),
      description: description.trim() || undefined,
      ...(kind ? { itemCategory: kind as ItemLanguageType } : {}),
      exit_status: 'active',
      tags: [],
      createdBy: userName || userId,
      workspace_id: getCurrentWorkspaceId() ?? undefined,
    } as Parameters<typeof createItem>[0]);
    setSubmitting(false);
    if (!ok) {
      setError('That could not be submitted. Try again, or check with an admin.');
      return;
    }
    setSubmitted(true);
    setTitle('');
    setDescription('');
    setKind('');
    setSuggestion(null);
  };

  return (
    <AppShell
      app={app}
      actions={
        <SecondaryButton onClick={() => navigate('/apps/intake/mine')} className="!px-3 !py-1.5">
          Mine
        </SecondaryButton>
      }
    >
      <ScreenHeading
        title="Drop an idea"
        subtitle="One mouth for ideas. Say it plainly — item work and routing happen after."
      />
      <BallLine ballWith={mayCreateItems ? "you — say what's on your mind" : ITEM_CREATE_BALL} />

      {submitted ? (
        <AppCard className="mb-4 border-alpine-green-200 dark:border-alpine-green-900/50">
          <div className="flex items-start gap-3">
            <Check className="mt-0.5 h-5 w-5 shrink-0 text-alpine-green-600" />
            <div>
              <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                Got it — that&apos;s in the queue
              </p>
              <p className="mt-1 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                A triager will pick it up. You can follow what happens to it under{' '}
                <button
                  type="button"
                  onClick={() => navigate('/apps/intake/mine')}
                  className="underline"
                >
                  Mine
                </button>
                .
              </p>
            </div>
          </div>
        </AppCard>
      ) : null}

      {mayCreateItems ? (
        <div className="space-y-4">
          <AppCard>
            <Eyebrow>What&apos;s the idea?</Eyebrow>
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setDuplicate(null);
              }}
              placeholder="e.g. Onboarding drops people at the invite step"
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Any detail worth knowing (optional)"
              className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
            />

            {craftEnabled && title.trim().length > 0 ? (
              <div className="mt-3">
                <SecondaryButton onClick={() => void shape()} disabled={shaping} className="!px-3 !py-1.5">
                  <Sparkles className="h-3.5 w-3.5" />
                  {shaping ? 'Wording…' : 'Help me word it'}
                </SecondaryButton>
                {shapeError ? (
                  <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                    {shapeError}
                  </p>
                ) : null}
              </div>
            ) : null}

            {suggestion ? (
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-[var(--rule)] dark:bg-[var(--surface-raised)]">
                <Eyebrow>Suggested wording</Eyebrow>
                <p className="mt-1 font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                  {suggestion.title}
                </p>
                {suggestion.description ? (
                  <p className="mt-1 font-body text-xs text-gray-600 dark:text-[var(--text-sec)]">
                    {suggestion.description}
                  </p>
                ) : null}
                <div className="mt-2 flex gap-2">
                  <PrimaryButton onClick={acceptSuggestion} className="!px-3 !py-1.5 !text-xs">
                    Use this
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setSuggestion(null)}
                    className="!px-3 !py-1.5 !text-xs"
                  >
                    Keep mine
                  </SecondaryButton>
                </div>
              </div>
            ) : null}
          </AppCard>

          <AppCard>
            <Eyebrow>What kind of thing is it?</Eyebrow>
            <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
              Not sure is a fine answer — a triager will sort it.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {kinds.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(kind === k.value ? '' : k.value)}
                  className={`rounded-md border px-3 py-1.5 font-body text-xs ${
                    kind === k.value
                      ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:text-primary-300'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
                  }`}
                  title={k.hint}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </AppCard>

          {duplicate ? (
            <AppCard>
              <p className="font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                This looks like one already on the list:{' '}
                <span className="font-medium">{duplicate.existingTitle}</span>
              </p>
              <div className="mt-3 flex gap-2">
                <PrimaryButton onClick={() => void submit(true)} className="!px-3 !py-1.5 !text-xs">
                  Send anyway
                </PrimaryButton>
                <SecondaryButton onClick={() => setDuplicate(null)} className="!px-3 !py-1.5 !text-xs">
                  Cancel
                </SecondaryButton>
              </div>
            </AppCard>
          ) : null}

          {error ? (
            <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end">
            <PrimaryButton onClick={() => void submit()} disabled={!canSubmit}>
              <Send className="h-4 w-4" /> {submitting ? 'Sending…' : 'Send it in'}
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="item-create-refused">
          {ITEM_CREATE_LINE}
        </p>
      )}
    </AppShell>
  );
}
